import React, { useState } from 'react';
import { Loader, X, GitMerge } from 'lucide-react';
import { previewContactMerge, type MergeableContact } from '../../lib/contact-merge';

export interface MergeContactGroup {
  key: string;
  /** null for the customer-wide group. */
  rigName: string | null;
  contacts: MergeableContact[];
}

export interface MergeContactDialogProps {
  /** The contact the action was opened on. It is the one that goes away. */
  from: MergeableContact;
  /** This client's OTHER live contacts, grouped by rig as the card groups them. */
  candidates: MergeContactGroup[];
  onConfirm: (keepId: string) => void;
  onCancel: () => void;
  pending: boolean;
  error: string | null;
}

const show = (v: string | null | undefined) => (v && v.trim() ? v : '—');

/**
 * "Merge {this} into…": pick the contact to KEEP. The preview applies the
 * server's fill rule (lib/contact-merge) so the office sees the result before
 * the write; it is never shown as the outcome — after success the card renders
 * the row the server returned.
 */
export default function MergeContactDialog({ from, candidates, onConfirm, onCancel, pending, error }: MergeContactDialogProps) {
  const [keepId, setKeepId] = useState<string | null>(null);
  const keep = candidates.flatMap((g) => g.contacts).find((c) => c.id === keepId) ?? null;
  const preview = keep ? previewContactMerge(keep, from) : null;
  const fromName = from.name?.trim() || 'this unnamed contact';

  // The modal layer; the bottom bar sits below it (lib/layers.ts).
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="merge-contact-title"
        className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col border border-fieldiq-border"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-fieldiq-border">
          <div className="flex items-center gap-2 min-w-0">
            <GitMerge size={18} className="text-fieldiq-accent flex-shrink-0" />
            <h3 id="merge-contact-title" className="text-base font-bold text-white">
              Merge <span className="break-words">{fromName}</span> into…
            </h3>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onCancel}
            disabled={pending}
            className="inline-flex items-center justify-center w-10 h-10 -m-2 rounded-lg text-fieldiq-muted hover:text-white transition-colors flex-shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-4">
          <p className="text-sm text-fieldiq-text">
            Pick the contact to keep. {fromName} is removed and its phone, email or role fill any
            the kept contact is missing.
          </p>

          <fieldset>
            <legend className="sr-only">Contact to keep</legend>
            <div className="space-y-3">
              {candidates.map((group) => (
                <div key={group.key} data-testid="merge-candidate-group">
                  <p className="text-xs font-semibold text-fieldiq-text mb-1" data-testid="merge-candidate-group-name">
                    {group.rigName ?? 'All rigs'}
                  </p>
                  <div className="border border-fieldiq-border rounded-lg divide-y divide-fieldiq-border">
                    {group.contacts.map((c) => (
                      <label
                        key={c.id}
                        data-testid="merge-candidate"
                        className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                          keepId === c.id ? 'bg-fieldiq-accent/15' : 'hover:bg-fieldiq-dark/60'
                        }`}
                      >
                        <input
                          type="radio"
                          name="merge-keep"
                          value={c.id}
                          checked={keepId === c.id}
                          onChange={() => setKeepId(c.id)}
                          className="mt-1"
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-white">{show(c.name)}</span>
                          <span className="block text-xs text-fieldiq-muted break-all">
                            {[c.phone, c.email].filter((v) => v && v.trim()).join(' · ') || '—'}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </fieldset>

          {preview && (
            <section aria-labelledby="merge-preview-title" data-testid="merge-preview" className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5">
              <h4 id="merge-preview-title" className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted mb-2">
                After the merge
              </h4>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-fieldiq-muted">Name</dt>
                <dd className="text-white break-words" data-testid="preview-name">{show(preview.name)}</dd>
                <dt className="text-fieldiq-muted">Rig</dt>
                <dd className="text-white break-words" data-testid="preview-rig">{preview.rigName?.trim() || 'All rigs'}</dd>
                <dt className="text-fieldiq-muted">Phone</dt>
                <dd className="text-white break-words" data-testid="preview-phone">{show(preview.phone)}</dd>
                <dt className="text-fieldiq-muted">Email</dt>
                <dd className="text-white break-all" data-testid="preview-email">{show(preview.email)}</dd>
                <dt className="text-fieldiq-muted">Role</dt>
                <dd className="text-white break-words" data-testid="preview-role">{show(preview.role)}</dd>
              </dl>
            </section>
          )}

          {error && (
            <p role="alert" className="text-sm text-fieldiq-text bg-fieldiq-danger/15 border border-fieldiq-danger/40 rounded-lg p-3">
              {error}
            </p>
          )}

          <p className="text-xs text-fieldiq-muted">Past jobs and tickets keep their own copy.</p>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-fieldiq-border">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="px-4 py-2 text-sm text-white hover:text-fieldiq-accent transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => keepId && onConfirm(keepId)}
            disabled={!keepId || pending}
            className="flex items-center gap-2 px-5 py-2 bg-fieldiq-accent text-fieldiq-dark font-bold rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pending && <Loader size={14} className="animate-spin" />}
            {pending ? 'Merging...' : 'Merge'}
          </button>
        </div>
      </div>
    </div>
  );
}
